"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
exports.MillenniumDateTime = void 0;
var underscore_1 = __importDefault(require("underscore"));
var MillenniumDate_1 = require("./MillenniumDate");
var MillenniumTime_1 = require("./MillenniumTime");
var MillenniumWeek_1 = require("./MillenniumWeek");
var MillenniumWeekday_1 = require("./MillenniumWeekday");
var SimpleDateFormat_1 = require("./SimpleDateFormat");
var MIN_DATE_TIME = 0;
var MAX_DATE_TIME = 2145916799;
var MillenniumDateTime = /** @class */ (function () {
    function MillenniumDateTime(millenniumTimeStampOrDate, millenniumTime) {
        if (millenniumTime === undefined) {
            if (millenniumTimeStampOrDate instanceof MillenniumDateTime) {
                this.mts = millenniumTimeStampOrDate.getMts();
            }
            else if (typeof millenniumTimeStampOrDate === "number") {
                if (millenniumTimeStampOrDate > -1 && millenniumTimeStampOrDate <= MAX_DATE_TIME) {
                    this.mts = millenniumTimeStampOrDate;
                }
                else if (millenniumTimeStampOrDate > MAX_DATE_TIME) {
                    throw new Error("A MillenniumDateTime may not have a timestamp greater than ".concat(MAX_DATE_TIME));
                }
                else if (millenniumTimeStampOrDate < MIN_DATE_TIME) {
                    throw new Error("A MilleniumDateTime may not have a timestamp less than ".concat(MIN_DATE_TIME));
                }
            }
            else {
                throw new Error("A MillenniumDateTime can not be created from the data type of ".concat(millenniumTimeStampOrDate));
            }
        }
        else {
            this.mts =
                millenniumTimeStampOrDate.getDayNumber() * 86400 +
                    millenniumTime.getTimeNumber();
            if (this.mts > MAX_DATE_TIME) {
                throw new Error("A MillenniumDateTime may not have a timestamp greater than ".concat(MAX_DATE_TIME));
            }
            else if (this.mts < MIN_DATE_TIME) {
                throw new Error("A MilleniumDateTime may not have a timestamp less than ".concat(MIN_DATE_TIME));
            }
        }
        if (this.mts === null) {
            throw new Error("Attempted to create MillenniumDateTime with null time");
        }
        if (isNaN(this.mts)) {
            throw new Error("Attempted to create MillenniumDateTime with NaN time");
        }
    }
    MillenniumDateTime.fromYyyyMMddHHmmss = function (yearString, monthString, dayString, hourString, minuteString, secondString, accept2400) {
        var year = parseInt(yearString, 10) || 0;
        if (year < MillenniumDate_1.MillenniumDate.MIN_YEAR || year > MillenniumDate_1.MillenniumDate.MAX_YEAR) {
            throw new Error("Year ".concat(year, " out of bounds."));
        }
        var month = parseInt(monthString, 10) || 0;
        if (month < 1 || month > 12) {
            throw new Error("Month ".concat(month, " out of bounds."));
        }
        var day = parseInt(dayString, 10) || 0;
        if (day < 1 || day > 31) {
            throw new Error("Day of month ".concat(day, " out of bounds."));
        }
        var hours = parseInt(hourString, 10) || 0;
        if (hours < 0 || hours > 24) {
            throw new Error("Hour of day ".concat(hours, " out of bounds."));
        }
        var minutes = parseInt(minuteString, 10) || 0;
        if (minutes < 0 || minutes > 59) {
            throw new Error("Minutes ".concat(minutes, " out of bounds."));
        }
        var seconds = parseInt(secondString, 10) || 0;
        if (seconds < 0 || seconds > 59) {
            throw new Error("Seconds ".concat(seconds, " out of bounds."));
        }
        if (hours === 24) {
            if (!accept2400 || minutes !== 0 || seconds !== 0) {
                throw new Error("Hour could not be set to 24.");
            }
        }
        var dayNumber = MillenniumDate_1.MillenniumDate.create(year, month, day).getDayNumber();
        var mts = dayNumber * 86400 + hours * 3600 + minutes * 60 + seconds;
        if (mts >= MillenniumDateTime.MIN_DATE_TIME && mts <= MillenniumDateTime.MAX_DATE_TIME) {
            return new MillenniumDateTime(mts);
        }
        throw new Error("Millennium timestamp out of bounds.");
    };
    MillenniumDateTime.fromYyyyMMddHHmmssString = function (dts) {
        if (dts === null || dts.length !== 14) {
            return null;
        }
        return MillenniumDateTime.fromYyyyMMddHHmmss(dts.substring(0, 4), // yyyy
        dts.substring(4, 6), // MM
        dts.substring(6, 8), // dd
        dts.substring(8, 10), // HH
        dts.substring(10, 12), // mm
        dts.substring(12, 14) // ss
        );
    };
    MillenniumDateTime.fromYyyyMMdd = function (dateString) {
        return MillenniumDateTime.fromDate(MillenniumDate_1.MillenniumDate.fromYyyyMMdd(dateString));
    };
    MillenniumDateTime.fromIcal = function (ical, accept2400) {
        var length = ical !== null ? ical.length : 0;
        if (ical === null || length < 15 || length > 16) {
            return null;
        }
        ical = ical.toLowerCase();
        if (ical.charAt(8) !== "t") {
            return null;
        }
        if (ical.length === 16 && ical.charAt(15) !== "z") {
            return null;
        }
        return MillenniumDateTime.fromYyyyMMddHHmmss(ical.substring(0, 4), // yyyy
        ical.substring(4, 6), // MM
        ical.substring(6, 8), // dd
        ical.substring(9, 11), // HH
        ical.substring(11, 13), // mm
        ical.substring(13, 15), // ss
        accept2400);
    };
    MillenniumDateTime.fromYearMonthDay = function (year, month, day) {
        return MillenniumDateTime.fromDate(MillenniumDate_1.MillenniumDate.create(year, month, day));
    };
    /**
     * @param date
     * @return A new MillenniumDateTime set to the start of the given date.
     */
    MillenniumDateTime.fromDate = function (date) {
        if (date === null) {
            return null;
        }
        return new MillenniumDateTime(date.getDayNumber() * 86400);
    };
    MillenniumDateTime.fromDayNumber = function (dayNumber) {
        return new MillenniumDateTime(dayNumber * 86400);
    };
    MillenniumDateTime.createFromList = function (list
    // TODO: This methods returns too many type variants. We need to fix this.
    ) {
        var isInstanceOf = function (obj, Type) {
            return obj instanceof Type || (Array.isArray(obj) && obj[0] instanceof Type);
        };
        var asArray = function (val) { return (Array.isArray(val) ? val : [val]); };
        var categories = {};
        list.forEach(function (item) {
            if (isInstanceOf(item, MillenniumTime_1.MillenniumTime)) {
                categories.time = item;
            }
            else if (isInstanceOf(item, MillenniumDate_1.MillenniumDate)) {
                categories.date = item;
            }
            else if (isInstanceOf(item, MillenniumWeek_1.MillenniumWeek)) {
                categories.week = item;
            }
            else if (isInstanceOf(item, MillenniumWeekday_1.MillenniumWeekday)) {
                categories.weekday = item;
            }
        });
        var dates = null;
        if (categories.date) {
            dates = asArray(categories.date).map(function (item) { return MillenniumDateTime.fromDate(item); });
        }
        else if (categories.week && categories.weekday) {
            dates = underscore_1["default"].flatten(asArray(categories.week).map(function (week) {
                var startOfWeek = week.getStartOfWeek();
                return asArray(categories.weekday).map(function (weekday) {
                    return startOfWeek.addDays(weekday.daysFromWeekday(week.getFirstDayOfWeek()));
                });
            }));
        }
        else {
            throw new Error("Could not create a date from the given list.");
        }
        if (categories.time) {
            var categoriesTimeArr_1 = asArray(categories.time);
            dates = underscore_1["default"].flatten(dates === null || dates === void 0 ? void 0 : dates.map(function (item) {
                return categoriesTimeArr_1.map(function (time) { return item === null || item === void 0 ? void 0 : item.addSeconds(time.getTimeNumber()); });
            }));
        }
        if ((dates === null || dates === void 0 ? void 0 : dates.length) === 1) {
            return dates[0];
        }
        dates === null || dates === void 0 ? void 0 : dates.sort(function (a, b) { return a.getMts() - b.getMts(); });
        return dates;
    };
    MillenniumDateTime.lengthInDays = function (begin, end) {
        if (end.isAfter(begin)) {
            return (end.getMillenniumDate().getDayNumber() - begin.getMillenniumDate().getDayNumber());
        }
        return begin.getMillenniumDate().getDayNumber() - end.getMillenniumDate().getDayNumber();
    };
    /* Instance functions */
    MillenniumDateTime.prototype.getMts = function () {
        return this.mts;
    };
    MillenniumDateTime.prototype.addYears = function (years) {
        var date = this.getMillenniumDate();
        var year = date.getYear();
        year = year + years;
        var month = date.getMonth(); // Returns 0 to 11.
        month++;
        var day = date.getDate();
        return new MillenniumDateTime(MillenniumDate_1.MillenniumDate.create(year, month, day), this.getMillenniumTime());
    };
    MillenniumDateTime.prototype.addMonths = function (months) {
        var date = this.getMillenniumDate();
        var year = date.getYear();
        var month = date.getMonth(); // Returns 0 to 11.
        month++;
        var startMonth = month;
        month = month + months;
        if (month > 12) {
            year = year + Math.floor(month / 12);
            month = month % 12;
        }
        else if (month === 0) {
            year--;
            month = 12;
        }
        else if (month < 0) {
            if (Math.floor(month / 12) === 0) {
                year--;
            }
            else {
                year = year + Math.floor(month / 12);
            }
            var monthsLeft = month % 12;
            if (-monthsLeft > startMonth) {
                year--;
                monthsLeft = 12 + monthsLeft;
            }
            else {
                monthsLeft = 12 + monthsLeft;
            }
            month = monthsLeft;
        }
        var day = date.getDate();
        if (month === 0) {
            month = 12;
            year--;
        }
        return new MillenniumDateTime(MillenniumDate_1.MillenniumDate.create(year, month, day), this.getMillenniumTime());
    };
    /**
     * @return A new MillenniumDateTime days number of days after the invoked MillenniumDateTime.
     */
    MillenniumDateTime.prototype.addDays = function (days) {
        return new MillenniumDateTime(this.mts + days * 86400);
    };
    /**
     * @return A new MillenniumDateTime weeks number of weeks after the invoked MillenniumDateTime.
     */
    MillenniumDateTime.prototype.addWeeks = function (weeks) {
        return this.addDays(weeks * 7);
    };
    /**
     * @return A new MillenniumDateTime seconds number of seconds after the invoked MillenniumDateTime.
     */
    MillenniumDateTime.prototype.addSeconds = function (seconds) {
        return new MillenniumDateTime(this.mts + seconds);
    };
    /**
     * @return A MillenniumDate object
     */
    MillenniumDateTime.prototype.getMillenniumDate = function () {
        return new MillenniumDate_1.MillenniumDate(Math.floor(this.mts / 86400));
    };
    /**
     * @return A MillenniumTime object
     */
    MillenniumDateTime.prototype.getMillenniumTime = function () {
        return new MillenniumTime_1.MillenniumTime(this.mts % 86400);
    };
    MillenniumDateTime.prototype.isMidnight = function () {
        return this.mts % 86400 === 0;
    };
    MillenniumDateTime.prototype.isBefore = function (otherDateTime) {
        return this.mts < otherDateTime.mts;
    };
    MillenniumDateTime.prototype.isAfter = function (otherDateTime) {
        return this.mts > otherDateTime.mts;
    };
    /** 0 - 23 */
    MillenniumDateTime.prototype.getHours = function () {
        return Math.floor((this.mts % 86400) / 3600);
    };
    MillenniumDateTime.prototype.getMinutes = function () {
        return Math.floor(((this.mts % 86400) % 3600) / 60);
    };
    MillenniumDateTime.prototype.getSeconds = function () {
        return Math.floor(((this.mts % 86400) % 3600) % 60);
    };
    /**
     * @return A new MillenniumDateTime at the start of the day of the invoked MillenniumDateTime.
     */
    MillenniumDateTime.prototype.getStartOfDay = function () {
        return new MillenniumDateTime(this.mts - (this.mts % 86400));
    };
    /**
     * @return A new MillenniumDateTime at the end of the day of the invoked MillenniumDateTime.
     */
    MillenniumDateTime.prototype.getEndOfDay = function () {
        return new MillenniumDateTime(this.mts - (this.mts % 86400) + 86400);
    };
    MillenniumDateTime.prototype.daysBetween = function (other) {
        return this.getMillenniumDate().daysBetween(other.getMillenniumDate());
    };
    MillenniumDateTime.prototype.getYear = function () {
        return this.getMillenniumDate().getYear();
    };
    /**
     * Returns 0 to 11.
     */
    MillenniumDateTime.prototype.getMonth = function () {
        return this.getMillenniumDate().getMonth();
    };
    /**
     * Return date in month (1-31).
     * @return {int} date in month
     */
    MillenniumDateTime.prototype.getDate = function () {
        return this.getMillenniumDate().getDate();
    };
    /**
     * Return the day of week as an index between 0 and 6.
     * @return {int} day of week
     */
    MillenniumDateTime.prototype.getDay = function () {
        return this.getMillenniumDate().getDay();
    };
    /**
     * Return the week number of the date. The default return value includes the year,
     * for example so that week 50 of 2012 returns 1250. Setting the includeYear parameter
     * to false would return only the week, i.e. 50.
     * @param  {boolean} includeYear should the return value include the year
     * @return {int}             week for date
     */
    MillenniumDateTime.prototype.getWeek = function (includeYear, firstDayOfWeek, daysInFirstWeek) {
        return this.getMillenniumDate().getWeek(includeYear, firstDayOfWeek, daysInFirstWeek);
    };
    MillenniumDateTime.prototype.getWeekYear = function (firstDayOfWeek, daysInFirstWeek) {
        return this.getMillenniumDate().getWeekYear(firstDayOfWeek, daysInFirstWeek);
    };
    MillenniumDateTime.prototype.equals = function (obj) {
        if (this === obj) {
            return true;
        }
        if (obj === null || obj === undefined) {
            return false;
        }
        if (!(obj instanceof MillenniumDateTime)) {
            return false;
        }
        return this.mts === obj.mts;
    };
    MillenniumDateTime.prototype.format = function (format) {
        format = format || "yyyyMMdd HH:mm:ss";
        return SimpleDateFormat_1.SimpleDateFormat.format(this, format);
    };
    MillenniumDateTime.prototype.toIcal = function (isUtc) {
        var result = this.format("yyyyMMddTHHmmss");
        if (isUtc) {
            return "".concat(result, "Z");
        }
        return result;
    };
    MillenniumDateTime.prototype.toString = function () {
        return this.format();
    };
    MillenniumDateTime.ZERO = new MillenniumDateTime(0);
    MillenniumDateTime.MIN_DATE_TIME = MIN_DATE_TIME;
    MillenniumDateTime.MAX_DATE_TIME = MAX_DATE_TIME;
    MillenniumDateTime.INVALID_DATE_TIME = null;
    MillenniumDateTime.UNDEFINED_DATE_TIME = 2147483647;
    return MillenniumDateTime;
}());
exports.MillenniumDateTime = MillenniumDateTime;
